import React, { useState } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Switch, Button, message, Space, Card } from 'antd';
import { SaveOutlined, LeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Competition, CompetitionType, UpdateCompetitionRequest } from '../../../core/competition/types';
import { updateCompetition } from '../../../core/competition/competitionApi';
import { AppDispatch, CombinedStates } from '../../../core/types';
import { subjectOptions, competitionTypeOptions, gradeOptions } from '../../../core/utils/helpers';

const { Option } = Select;

interface CompetitionGeneralDetailsProps {
    competition?: Competition | null;
    showBackButton?: boolean;
    showCard?: boolean;
}

const CompetitionGeneralDetails: React.FC<CompetitionGeneralDetailsProps> = ({
    competition: propCompetition,
    showBackButton = false,
    showCard = true,
}) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const {
        currentCompetition,
        isFetching,
    } = useSelector((state: CombinedStates) => state.competition);

    // Use prop competition if provided, otherwise use currentCompetition from state
    const competition = propCompetition || currentCompetition;

    const handleBack = () => {
        navigate('/svietimo-centro-rodikliai', {
            state: { activeTab: '2' }, // '2' is the key for "Konkursai" tab
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const updateData: UpdateCompetitionRequest = {
                id: competition!.id!,
                ...values,
                date: values.date.format('YYYY-MM-DD'),
                imported: values.imported || false,
            };

            await dispatch(updateCompetition(updateData)).unwrap();
            message.success('Konkursas sėkmingai atnaujintas!');

            // Navigate to competition detail page
            navigate('/konkursas');
        } catch (error) {
            message.error('Klaida atnaujinant konkursą');
        } finally {
            setLoading(false);
        }
    };

    if (!competition) {
        return <div>Konkursas nerastas</div>;
    }

    const formContent = (
        <Form
            form={form}
            layout="vertical"
            initialValues={{
                ...competition,
                date: dayjs(competition.date),
            }}>
            <Form.Item
                name="title"
                label="Pavadinimas"
                rules={[{ required: true, message: 'Prašome įvesti pavadinimą' }]}>
                <Input />
            </Form.Item>

            <Form.Item
                name="subject"
                label="Dalykas"
                rules={[{ required: true, message: 'Prašome įvesti dalyką' }]}>
                <Select>
                    {subjectOptions.map(subject => (
                        <Option key={subject.value} value={subject.value}>
                            {subject.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="type"
                label="Tipas"
                rules={[{ required: true, message: 'Prašome pasirinkti tipą' }]}>
                <Select>
                    {competitionTypeOptions.map(type => (
                        <Option key={type.value} value={type.value}>
                            {type.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="date"
                label="Data"
                rules={[{ required: true, message: 'Prašome pasirinkti datą' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item
                name="grade_from"
                label="Konkurso klasė nuo"
                rules={[{ required: true, message: 'Prašome pasirinkti konkurso klasę nuo' }]}>
                <Select placeholder="Pasirinkite klasę nuo">
                    {gradeOptions.map(grade => (
                        <Option key={grade.value} value={grade.value}>
                            {grade.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="grade_to"
                label="Konkurso klasė iki"
                rules={[{ required: true, message: 'Prašome pasirinkti konkurso klasę iki' }]}>
                <Select placeholder="Pasirinkite klasę iki">
                    {gradeOptions.map(grade => (
                        <Option key={grade.value} value={grade.value}>
                            {grade.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="participants_total"
                label="Dalyvių skaičius"
                rules={[{ required: true, message: 'Prašome įvesti dalyvių skaičių' }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
                name="organizer"
                label="Organizatorius"
                rules={[{ required: true, message: 'Prašome įvesti organizatorių' }]}>
                <Input />
            </Form.Item>

            <Form.Item>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}>
                    Išsaugoti
                </Button>
            </Form.Item>
        </Form>
    );

    if (showCard) {
        return (
            <Card title="Redaguoti konkursą">
                {showBackButton && (
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        onClick={handleBack}
                        style={{ marginBottom: '16px' }}>
                        Grįžti atgal
                    </Button>
                )}
                {formContent}
            </Card>
        );
    }

    return (
        <>
            {showBackButton && (
                <Button
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={handleBack}
                    style={{ marginBottom: '16px' }}>
                    Grįžti atgal
                </Button>
            )}
            {formContent}
        </>
    );
};

CompetitionGeneralDetails.defaultProps = {
    competition: null,
    showBackButton: false,
    showCard: true,
};

export default CompetitionGeneralDetails;
